<template>
  <span class="filter-button-host">
    <button
      type="button"
      :class="[buttonClass, { highlight: active }]"
      :aria-expanded="String(active)"
      :title="title"
      @click="$emit('click', $event)"
    >
      <slot>{{ label }}</slot>
      <span v-if="activeFilterCount > 0" class="filter-button__badge">{{ activeFilterCount }}</span>
    </button>
    <div
      v-if="filterEntries.length > 0"
      class="filter-button__tooltip"
      role="tooltip"
    >
      <div class="filter-button__tooltip-panel">
        <div v-for="entry in filterEntries" :key="entry.key" class="filter-button__tooltip-row">
          <strong>{{ entry.label }}:</strong> {{ entry.value }}
        </div>
        <button
          type="button"
          class="filter-button__clear"
          @click.stop="$emit('clear-filters')"
        >
          Clear Filters
        </button>
      </div>
    </div>
  </span>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  filter: {
    type: Object,
    default: () => ({}),
  },
  filterCount: {
    type: Number,
    default: null,
  },
  active: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: 'Filter',
  },
  title: {
    type: String,
    default: 'Toggle Filter Panel',
  },
  buttonClass: {
    type: [String, Array, Object],
    default: 'button-light',
  },
});

defineEmits(['click', 'clear-filters']);

const formatKey = (key) => String(key)
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/[_.-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const isSet = (value) => {
  if (value === null || value === undefined || value === false) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    return Object.entries(value).some(([key, item]) => key !== 'type' && isSet(item));
  }
  return typeof value === 'number' ? Number.isFinite(value) : Boolean(value);
};

const formatValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (item && typeof item === 'object') {
        return item.name ?? item.label ?? item.value ?? item.key ?? item.id ?? JSON.stringify(item);
      }
      return String(item);
    }).join(', ');
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value && typeof value === 'object') {
    const displayValue = value.name ?? value.label ?? value.value ?? value.key;
    if (displayValue !== undefined) return String(displayValue);
    return Object.entries(value)
      .filter(([key, item]) => key !== 'type' && isSet(item))
      .map(([key, item]) => `${formatKey(key)} ${formatValue(item)}`)
      .join(', ');
  }
  return String(value);
};

const collectEntries = (value, prefix = '') => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];

  return Object.entries(value).flatMap(([key, item]) => {
    if (key === 'type' && prefix.toLowerCase().includes('bounding box')) return [];
    if (!isSet(item)) return [];
    if (key === 'dynamicFilters') return collectEntries(item, prefix);

    const label = prefix ? `${prefix} ${formatKey(key)}` : formatKey(key);
    if (Array.isArray(item)) return [{ key: label, label, value: formatValue(item) }];
    if (item && typeof item === 'object') return collectEntries(item, label);
    return [{ key: label, label, value: formatValue(item) }];
  });
};

const filterEntries = computed(() => collectEntries(props.filter));
const activeFilterCount = computed(() => (
  props.filterCount === null ? filterEntries.value.length : props.filterCount
));
</script>

<style lang="scss" scoped>
.filter-button-host {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.filter-button__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 999px;
  background: #e53935;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
}

.filter-button__tooltip {
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 900;
  display: none;
  padding-top: 8px;
  pointer-events: auto;
}

.filter-button__tooltip-panel {
  min-width: 200px;
  max-width: 360px;
  max-height: 280px;
  overflow: auto;
  padding: 10px 12px;
  border: 1px solid rgba(120, 120, 120, 0.35);
  border-radius: 6px;
  background: #fff;
  color: #222;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
  font-size: 12px;
  line-height: 1.45;
  white-space: normal;
}

.filter-button-host:hover .filter-button__tooltip,
.filter-button-host:focus-within .filter-button__tooltip {
  display: block;
}

.filter-button__tooltip-row + .filter-button__tooltip-row {
  margin-top: 4px;
}

.filter-button__tooltip-row strong {
  font-weight: 600;
}

.filter-button__clear {
  display: block;
  width: 100%;
  margin-top: 10px;
  padding: 7px 10px;
  border: 0;
  border-top: 1px solid rgba(120, 120, 120, 0.25);
  background: transparent;
  color: #c62828;
  font: inherit;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.filter-button__clear:hover {
  text-decoration: underline;
}
</style>
